import React from "react";
import { useTranslation } from "../../useTranslation";
import { Row, Column, H3, Box } from "@vp/swan";
import GoogleButton from "./GoogleButton";
import FacebookButton from "./FacebookButton";
import SocialLoginButtons from "./SocialLoginButtons";
import "./withSocialProviders.scss";
import AppleButton from "./AppleButton";
import ShowOptionButton from "./ShowSocialOptionButton";

interface WithSocialProvidersProps {
  context: "signIn" | "signUp";
  children: React.ReactNode;
  showSocialSignIn?: boolean;
  setShowSocialSignIn: Function;
}

interface WithSocialProvidersProps {
  context: "signIn" | "signUp";
  children: React.ReactNode;
  isSocialLoginDisabledForLocale: Boolean;
  showSocialSignIn?: boolean;
  setShowSocialSignIn: Function;
  showLoginWithEmail?: boolean;
  setShowLoginWithEmail: Function;
}

type SocialLoginProps = Pick<WithSocialProvidersProps, "context">;
type i18nProps = {
  nameSpace: string;
};

export const SocialLogin: React.FC<SocialLoginProps & i18nProps> = ({
  context,
  nameSpace = "social"
}) => {
  const [t] = useTranslation(nameSpace);
  return (
    <>
      <H3>{t(`heading.${context}`)}</H3>
      <Box mb="between-actions">
        <GoogleButton
          id={`google-${context}`}
          title={t(`google.${context}.buttonTittle`)}
          imgAlt={t(`google.${context}.imgAlt`)}
        />
      </Box>
      <Box mb="between-actions">
        <FacebookButton
          id={`facebook-${context}`}
          title={t(`facebook.${context}.buttonTittle`)}
          imgAlt={t(`facebook.${context}.imgAlt`)}
        />
      </Box>
      <AppleButton
        id={`apple-${context}`}
        title={t(`apple.${context}.buttonTittle`)}
        imgAlt={t(`apple.${context}.imgAlt`)}
      />
    </>
  );
};

const WithSocialProvidersControl = (props: WithSocialProvidersProps) => {
  const {
    children,
    context,
    showSocialSignIn = true,
    setShowSocialSignIn,
    showLoginWithEmail = false,
    setShowLoginWithEmail,
    isSocialLoginDisabledForLocale
  } = props;

  const [t] = useTranslation();
  const onShowSocialLoginClick = () => {
    setShowSocialSignIn(true);
  };

  const onShowLoginWithEmail = () => {
    setShowSocialSignIn(false);
    setShowLoginWithEmail(false);
  };

  const renderContent = () => {
    return (
      <>
        {!isSocialLoginDisabledForLocale && (
          <Box mb="between-subsections">
            {!showSocialSignIn && (
              <ShowOptionButton
                logo="arrowLeft"
                buttonText={t("social:heading.backToSocialSignIn")}
                onButtonClick={onShowSocialLoginClick}
              ></ShowOptionButton>
            )}
            {showSocialSignIn && (
              <SocialLoginButtons
                context={context}
                wrappedProps={{}}
              />
            )}
            {showLoginWithEmail && (
              <ShowOptionButton
                logo="mail"
                buttonText={t(`social:heading.${context}WithEmail`)}
                onButtonClick={onShowLoginWithEmail}
              ></ShowOptionButton>
            )}
          </Box>
        )}
        {children}
      </>
    );
  };

  return (
    <Row>
      <Column
        span={12}
        offset={0}
        spanSm={8}
        offsetSm={2}
        spanMd={6}
        offsetMd={3}
        spanLg={4}
        offsetLg={4}
        spanXl={4}
        offsetXl={4}
      >
        {renderContent()}
      </Column>
    </Row>
  );
};

const WithSocialProviders: React.FunctionComponent<WithSocialProvidersProps> = React.memo(
  WithSocialProvidersControl
);

export default WithSocialProviders;
